import React, { useMemo } from 'react';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { FiltersState } from 'apps/sigma2-ui/src/lib/hooks/filter-state';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';

/**
 * Mostly copied from https://github.com/uidotdev/usehooks/blob/546df269e31a9ba10e0f6902fbda66d5eab7caa7/index.js#L595
 * Modified to add types
 */

type PrimitiveValue = string | boolean | number | null;

interface GenericObject {
  [key: string]:
    | PrimitiveValue
    | GenericObject
    | Array<PrimitiveValue>
    | FiltersState
    | Array<GenericObject>
    | null;
}

type LocalStorageValue = PrimitiveValue | GenericObject;

const dispatchStorageEvent = (
  key: string,
  newValue: string | null | undefined,
): void => {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};

const setLocalStorageItem = <T extends LocalStorageValue>(
  key: string,
  value: T,
): void => {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getLocalStorageItem = (key: string) => {
  return window.localStorage.getItem(key);
};

const useLocalStorageSubscribe = (callback: () => void) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const getLocalStorageServerSnapshot = () => {
  throw Error('useLocalStorage is a client-only hook');
};

type ValueFnSetState<T extends LocalStorageValue> = (
  value: (existingValue: T) => T,
) => void;

/**
 * Helper for working with local storage. Use it like you would with `useState` but it'll sync with the local storage
 * @example const [somevalue, setSomeValue] = useLocalStorage({ key: 'some key', defaultvalue: false })
 */
export function useLocalStorage<Value extends LocalStorageValue>({
  key,
  schema,
  defaultValue,
}: {
  key: string;
  schema: S2SafeParsable<unknown, Value>;
  defaultValue: Value;
}): [Value, ValueFnSetState<Value>] {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = React.useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const storeValue = useMemo(() => {
    const parseResult = jsonParse(store ?? '', schema);

    if (parseResult.resultType === 'err') {
      return defaultValue;
    }

    return parseResult.value;
  }, [defaultValue, schema, store]);

  const setState = React.useCallback<ValueFnSetState<Value>>(
    (value) => {
      try {
        const nextState =
          typeof value === 'function' ? value(storeValue) : value;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        uiAppLogger.warn(e);
      }
    },
    [key, storeValue],
  );

  React.useEffect(() => {
    if (
      getLocalStorageItem(key) === null &&
      typeof defaultValue !== 'undefined'
    ) {
      setLocalStorageItem(key, defaultValue);
    }
  }, [key, defaultValue]);

  return useMemo(() => {
    return [storeValue, setState];
  }, [setState, storeValue]);
}
